import React, { useState, useEffect } from 'react';
import { 
  MdWorkOutline, 
  MdLocationOn, 
  MdAccessTime, 
  MdBusinessCenter,
  MdCalendarToday,
  MdSchool,
  MdPeople,
  MdLightbulb,
  MdAttachMoney,
  MdVerified
} from 'react-icons/md';
import { FaSeedling, FaArrowRight, FaUsers } from 'react-icons/fa';
import { GiGrowth } from 'react-icons/gi';
import { Link } from 'react-router-dom';

const Careers = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);

    return () => {
      window.removeEventListener('resize', checkViewport);
    };
  }, []);

  // Get today's date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const jobOpenings = [
    {
      id: 1,
      title: 'Agri-Tech Intern',
      department: 'Research & Development',
      type: 'Internship',
      location: 'Pune, Maharashtra',
      duration: '3-6 months',
      experience: 'Freshers/Students',
      postedDate: getCurrentDate(),
      compensation: 'Unpaid',
      certificateProvided: true,
      description: 'Work on cutting-edge agricultural technologies and smart farming solutions.',
      responsibilities: [
        'Assist in IoT-based farming solution development',
        'Support agricultural automation system testing',
        'Participate in farmer field testing programs',
        'Document technical specifications'
      ],
      requirements: [
        'B.Tech / M.Tech / B.Sc / M.Sc / B.Sc Agriculture',
        'Basic understanding of IoT technologies',
        'Interest in precision farming techniques',
        'Willingness to learn and contribute'
      ],
      keywords: 'agricultural technology internship, IoT farming internship, smart agriculture intern'
    },
    {
      id: 2,
      title: 'Field Operations Intern',
      department: 'Operations',
      type: 'Internship',
      location: 'Pune, Maharashtra',
      duration: '3-6 months',
      experience: 'Freshers/Students',
      postedDate: getCurrentDate(),
      compensation: 'Unpaid',
      certificateProvided: true,
      description: 'Gain hands-on experience in agricultural field operations and management.',
      responsibilities: [
        'Assist in field operations monitoring',
        'Support farmer training program coordination',
        'Help with project implementation tracking',
        'Collect field data and feedback'
      ],
      requirements: [
        'B.Tech / M.Tech / B.Sc / M.Sc / computer Science  ',
        'Interest in agricultural operations',
        'Good communication skills',
        'Willingness to travel to field sites'
      ],
      keywords: 'agricultural operations internship, field operations intern, farm management training'
    },
    {
      id: 3,
      title: 'Digital Marketing Intern',
      department: 'Marketing',
      type: 'Internship',
      location: 'Remote',
      duration: '3-6 months',
      experience: 'Freshers/Students',
      postedDate: getCurrentDate(),
      compensation: 'Unpaid',
      certificateProvided: true,
      description: 'Learn and contribute to digital marketing for sustainable farming solutions.',
      responsibilities: [
        'Assist in social media campaign management',
        'Help create educational content for farmers',
        'Support digital marketing strategy development',
        'Analyze campaign performance metrics'
      ],
      requirements: [
        'B.Tech / M.Tech / B.Sc / M.Sc ',
        'Interest in digital marketing',
        'Basic knowledge of social media platforms',
        'Good writing and communication skills'
      ],
      keywords: 'digital marketing internship, agricultural marketing intern, content marketing training'
    },
    {
      id: 4,
      title: 'Data Analytics Intern',
      department: 'Analytics',
      type: 'Internship',
      location: 'Remote',  // Changed to Remote
      duration: '6 months',
      experience: 'Freshers/Students',
      postedDate: getCurrentDate(),
      compensation: 'Unpaid',
      certificateProvided: true,
      description: 'Work with agricultural data to derive insights for farming improvement.',
      responsibilities: [
        'Assist in farm data analysis',
        'Support predictive model development',
        'Help create data visualization dashboards',
        'Collaborate with agronomy team'
      ],
      requirements: [
        'B.Tech / M.Tech / B.Sc / M.Sc (Agri Tech & Data Analysis)',
        'Basic knowledge of data analysis',
        'Interest in agricultural data systems',
        'Statistical analysis skills'
      ],
      keywords: 'agricultural data science internship, data analytics training, farming analytics intern'
    },
    {
      id: 5,
      title: 'Community Outreach Intern',
      department: 'Outreach',
      type: 'Internship',
      location: 'Multiple Locations',
      duration: '3-6 months',
      experience: 'Freshers/Students',
      postedDate: getCurrentDate(),
      compensation: 'Unpaid',
      certificateProvided: true,
      description: 'Engage with farming communities and support rural development initiatives.',
      responsibilities: [
        'Assist in farming community engagement',
        'Support farmer training workshop organization',
        'Help collect field data and feedback',
        'Support farmers in basic technology adoption'
      ],
      requirements: [
        'B.Tech / M.Tech / B.Sc / M.Sc  Computer Science',
        'Good communication skills in local languages',
        'Understanding of farming communities',
        'Fieldwork interest'
      ],
      keywords: 'community outreach internship, rural development intern, farmer engagement training'
    },
    {
      id: 6,
      title: 'Sustainability Intern',
      department: 'Sustainability',
      type: 'Internship',
      location: 'Remote',
      duration: '3-6 months',
      experience: 'Freshers/Students',
      postedDate: getCurrentDate(),
      compensation: 'Unpaid',
      certificateProvided: true,
      description: 'Learn about sustainable farming practices and environmental impact assessment.',
      responsibilities: [
        'Assist in sustainability framework development',
        'Support environmental impact assessments',
        'Help guide farmers on sustainable practices',
        'Prepare basic sustainability reports'
      ],
      requirements: [
        'B.Tech / M.Tech / B.Sc / M.Sc Computer Science',
        'Interest in agricultural sustainability',
        'Knowledge of basic environmental concepts',
        'Analytical thinking skills'
      ],
      keywords: 'sustainable agriculture internship, environmental science intern, green farming training'
    }
  ];

  const benefits = [
    {
      icon: <MdSchool />,
      title: 'Learning Opportunity',
      description: 'Hands-on experience with real projects'
    },
    {
      icon: <MdPeople />,
      title: 'Mentorship',
      description: 'Guidance from industry experts'
    },
    {
      icon: <MdLightbulb />,
      title: 'Innovation Exposure',
      description: 'Work with cutting-edge agricultural tech'
    },
    {
      icon: <GiGrowth />,
      title: 'Skill Development',
      description: 'Professional growth and training'
    },
    {
      icon: <FaUsers />,
      title: 'Networking',
      description: 'Connect with agricultural professionals'
    },
    {
      icon: <MdWorkOutline />,
      title: 'Future Opportunities',
      description: 'Potential for full-time positions'
    }
  ];

  return (
    <div className="premium-careers">
      {/* Hero Section */}
      <header className="hero-section" role="banner">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <FaSeedling />
                <span>Internship Opportunities</span>
              </div>
              
              <h1 className="hero-title">
                Launch Your Career in
                <span className="title-green">
                  Agricultural Technology
                </span>
              </h1>
              
              <p className="hero-description">
                Join Krishidnya's internship program to gain hands-on experience in transforming 
                Indian agriculture. Work with cutting-edge technology, learn from experts, and 
                contribute to meaningful projects.
              </p>
              
              <div className="hero-actions">
                <button 
                  className="btn-primary"
                  onClick={() => document.getElementById('intern-openings').scrollIntoView({ behavior: 'smooth' })}
                >
                  <span>View Internships</span>
                  <FaArrowRight className="btn-icon" />
                </button>
                <Link to="/about" className="btn-secondary">
                  About Our Culture
                </Link>
              </div>
            </div>
            
            <div className="hero-stats">
              <div className="stats-grid">
                <div className="stat-preview">
                  <div className="stat-preview-icon">
                    <MdWorkOutline />
                  </div>
                  <div className="stat-preview-content">
                    <div className="stat-preview-number">{jobOpenings.length}+</div>
                    <div className="stat-preview-label">Intern Positions</div>
                  </div>
                </div>
                <div className="stat-preview">
                  <div className="stat-preview-icon">
                    <MdLocationOn />
                  </div>
                  <div className="stat-preview-content">
                    <div className="stat-preview-number">5+</div>
                    <div className="stat-preview-label">Locations</div>
                  </div>
                </div>
                <div className="stat-preview">
                  <div className="stat-preview-icon">
                    <GiGrowth />
                  </div>
                  <div className="stat-preview-content">
                    <div className="stat-preview-number">100%</div>
                    <div className="stat-preview-label">Learning Focus</div>
                  </div>
                </div>
                <div className="stat-preview">
                  <div className="stat-preview-icon">
                    <MdPeople />
                  </div>
                  <div className="stat-preview-content">
                    <div className="stat-preview-number">10+</div>
                    <div className="stat-preview-label">Alumni Network</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="hero-bottom">
          <div className="container">
            <div className="scroll-indicator">
              <span>Scroll to explore opportunities</span>
              <div className="scroll-line"></div>
            </div>
          </div>
        </div>
      </header>

      {/* Benefits Section */}
      <section className="benefits-section" aria-labelledby="benefits-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="benefits-heading">Why Intern With Us</h2>
            <p className="section-description">
              Gain valuable experience and build your career in agricultural technology
            </p>
          </div>
          
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">
                  {benefit.icon}
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intern Openings */}
      <section id="intern-openings" className="jobs-section" aria-labelledby="jobs-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="jobs-heading">Current Internship Openings</h2>
            <p className="section-description">
              Explore internship opportunities to learn and grow with us
            </p>
          </div>
          
          <div className="jobs-grid">
            {jobOpenings.map((job) => (
              <article key={job.id} className="job-card">
                <div className="job-header">
                  <div className="job-badge">
                    <span className="job-type">{job.type}</span>
                    <span className="job-department">{job.department}</span>
                  </div>
                  <h3 className="job-title">{job.title}</h3>
                  <p className="job-description">{job.description}</p>
                </div>
                
                <div className="job-details">
                  <div className="detail-item">
                    <MdLocationOn />
                    <span>{job.location}</span>
                  </div>
                  <div className="detail-item">
                    <MdAccessTime />
                    <span>{job.duration}</span>
                  </div>
                  <div className="detail-item">
                    <MdBusinessCenter />
                    <span>{job.experience}</span>
                  </div>
                  <div className="detail-item">
                    <MdCalendarToday />
                    <span>Posted: {job.postedDate}</span>
                  </div>
                  <div className="detail-item">
                    <MdAttachMoney />
                    <span className={job.compensation === 'Paid' ? 'compensation-paid' : 'compensation-unpaid'}>
                      {job.compensation}
                    </span>
                  </div>
                </div>
                
                {/* Certificate & LOR Badge */}
                {job.certificateProvided && (
                  <div className="certificate-badge">
                    <MdVerified />
                    <span>Certificate & LOR Provided</span>
                  </div>
                )}
                
                <div className="job-actions">
                  <button 
                    className="btn-outline"
                    onClick={() => {
                      window.location.href = `mailto:hello@krishidnya.com?subject=Internship Application: ${job.title}&body=Dear Hiring Team,%0D%0A%0D%0AI am interested in the ${job.title} internship position.%0D%0A%0D%0APlease find my details below:%0D%0A%0D%0A`;
                    }}
                    aria-label={`Apply for ${job.title} internship`}
                  >
                    Apply Now
                  </button>
                  <button 
                    className="btn-text"
                    onClick={() => setSelectedJob(job === selectedJob ? null : job)}
                    aria-label={`View details for ${job.title}`}
                  >
                    {selectedJob?.id === job.id ? 'Hide Details' : 'View Details'}
                  </button>
                </div>
                
                {selectedJob?.id === job.id && (
                  <div className="job-expanded">
                    <div className="job-responsibilities">
                      <h4>What You'll Do:</h4>
                      <ul>
                        {job.responsibilities.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="job-requirements">
                      <h4>What We're Looking For:</h4>
                      <ul>
                        {job.requirements.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="job-perks">
                      <h4>Perks & Benefits:</h4>
                      <ul>
                        <li>{job.compensation === 'Paid' ? 'Competitive stipend' : 'Performance-based incentives'}</li>
                        <li>Certificate of Internship upon successful completion</li>
                        <li>Letter of Recommendation based on performance</li>
                        <li>Hands-on experience with real-world projects</li>
                        <li>Mentorship from industry experts</li>
                      </ul>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="process-section" aria-labelledby="process-heading">
        <div className="container">
          <div className="section-intro">
            <h2 id="process-heading">Application Process</h2>
            <p className="section-description">
              Simple steps to join our internship program
            </p>
          </div>
          
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Send Your Application</h3>
              <p>Email your resume and cover letter to careers@krishidnya.com</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>Screening Call</h3>
              <p>Initial conversation about your interests and goals</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Assessment</h3>
              <p>Brief task or discussion related to the internship</p>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Final Interview</h3>
              <p>Meet with team leads and department heads</p>
            </div>
            <div className="process-step">
              <div className="step-number">5</div>
              <h3>Offer & Onboarding</h3>
              <p>Welcome to the Krishidnya internship program!</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" aria-labelledby="cta-heading">
        <div className="container">
          <div className="cta-content">
            <h2 id="cta-heading">Ready to Start Your Journey?</h2>
            <p>
              Don't see a perfect match? We're always looking for passionate students 
              who want to learn and contribute to agricultural transformation.
            </p>
            
            <div className="cta-actions">
              <a 
                href="mailto:hello@krishidnya.com?subject=General Internship Inquiry - Krishidnya"
                className="btn-primary"
              >
                <span>Email Your Application</span>
                <FaArrowRight />
              </a>
              <Link to="/contact" className="btn-secondary">
                <span>Contact for Queries</span>
              </Link>
            </div>
            
            <div className="cta-info">
              <p>For any queries, contact us at <strong>hello@krishidnya.com</strong></p>
              <p>We typically respond within 3-5 business days</p>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Premium Design System */
        :root {
          --primary-black: #000000;
          --secondary-black: #1A1A1A;
          --medium-black: #333333;
          --light-black: #666666;
          --primary-green: #2E8B57;
          --secondary-green: #3A7D34;
          --light-green: #4CAF7A;
          --accent-green: #2D5A27;
          --white: #FFFFFF;
          --off-white: #FAFAFA;
          --light-gray: #F5F5F5;
          --medium-gray: #E8E8E8;
          --border-color: #E0E0E0;
          --text-primary: #000000;
          --text-secondary: #333333;
          --text-light: #666666;
          --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
          --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
          --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
          --radius-sm: 6px;
          --radius-md: 10px;
          --radius-lg: 16px;
          --radius-xl: 20px;
          --transition: all 0.3s ease;
          --header-height: ${isMobile ? '70px' : '80px'};
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .premium-careers {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: var(--text-primary);
          background: var(--white);
          line-height: 1.6;
          overflow-x: hidden;
          scroll-behavior: smooth;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 ${isMobile ? '20px' : '24px'};
        }

        /* Typography */
        h1, h2, h3, h4 {
          font-family: 'Source Serif Pro', serif;
          font-weight: 600;
          line-height: 1.2;
          letter-spacing: -0.01em;
          color: var(--primary-black);
        }

        h1 { 
          font-size: ${isMobile ? '2.25rem' : isTablet ? '3rem' : '3.5rem'};
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        h2 { 
          font-size: ${isMobile ? '1.75rem' : isTablet ? '2.25rem' : '2.5rem'};
          margin-bottom: 1rem;
        }

        h3 { 
          font-size: ${isMobile ? '1.25rem' : isTablet ? '1.5rem' : '1.75rem'};
          margin-bottom: 0.75rem;
        }

        p { 
          font-size: ${isMobile ? '1rem' : '1.125rem'};
          color: var(--text-secondary);
          line-height: 1.7;
          font-weight: 400;
        }

        .section-description {
          font-size: ${isMobile ? '1rem' : '1.125rem'};
          color: var(--text-light);
          margin-top: 0.5rem;
          font-weight: 400;
        }

        /* Buttons */
        .btn-primary, .btn-secondary, .btn-outline, .btn-text {
          padding: ${isMobile ? '12px 24px' : '14px 32px'};
          font-size: ${isMobile ? '0.95rem' : '1rem'};
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          border-radius: var(--radius-md);
          text-decoration: none;
          transition: var(--transition);
          border: 2px solid transparent;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          text-align: center;
          min-width: ${isMobile ? '140px' : 'auto'};
        }

        .btn-primary {
          background: var(--primary-black);
          color: var(--white);
        }

        .btn-primary:hover, .btn-primary:focus {
          background: var(--secondary-black);
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
          outline: 2px solid var(--primary-green);
        }

        .btn-secondary {
          background: transparent;
          color: var(--primary-black);
          border: 2px solid var(--primary-black);
        }

        .btn-secondary:hover, .btn-secondary:focus {
          background: var(--primary-black);
          color: var(--white);
          transform: translateY(-2px);
          outline: 2px solid var(--primary-green);
        }

        .btn-outline {
          background: transparent;
          color: var(--primary-green);
          border: 2px solid var(--primary-green);
          padding: 10px 20px;
          font-size: 0.875rem;
        }

        .btn-outline:hover, .btn-outline:focus {
          background: var(--primary-green);
          color: var(--white);
        }

        .btn-text {
          background: transparent;
          color: var(--text-light);
          border: none;
          padding: 10px 0;
          font-size: 0.875rem;
          text-decoration: underline;
        }

        .btn-text:hover, .btn-text:focus {
          color: var(--primary-green);
        }

        .btn-icon {
          font-size: ${isMobile ? '1rem' : '1.125rem'};
          transition: transform 0.3s ease;
        }

        .btn-primary:hover .btn-icon {
          transform: translateX(4px);
        }

        /* Hero Section */
        .hero-section {
          padding: ${isMobile ? 'calc(var(--header-height) + 40px) 0 40px' : 'calc(var(--header-height) + 60px) 0 60px'};
          background: var(--white);
          position: relative;
          margin-top: 0;
        }

        .hero-section::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--border-color), transparent);
        }

        .hero-content {
          display: flex;
          flex-direction: ${isMobile ? 'column' : 'row'};
          gap: ${isMobile ? '40px' : '60px'};
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .hero-text {
          flex: 1;
        }

        .hero-stats {
          flex: ${isMobile ? '0 0 auto' : '1'};
          width: ${isMobile ? '100%' : 'auto'};
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(46, 139, 87, 0.1);
          color: var(--primary-green);
          padding: ${isMobile ? '8px 16px' : '10px 20px'};
          border-radius: var(--radius-round);
          font-size: ${isMobile ? '0.85rem' : '14px'};
          font-weight: 600;
          margin-bottom: ${isMobile ? '20px' : '30px'};
          border: 1px solid rgba(46, 139, 87, 0.2);
        }

        .hero-title {
          margin-bottom: ${isMobile ? '1rem' : '1.5rem'};
          line-height: 1.1;
        }

        .title-green {
          color: var(--primary-green);
          display: block;
          margin-top: ${isMobile ? '0.5rem' : '0.75rem'};
        }

        .hero-description {
          margin-bottom: ${isMobile ? '2rem' : '3rem'};
          font-size: ${isMobile ? '1.063rem' : '1.125rem'};
          line-height: 1.6;
        }

        .hero-actions {
          display: flex;
          gap: ${isMobile ? '0.75rem' : '1rem'};
          margin-bottom: ${isMobile ? '3rem' : '0'};
          flex-direction: ${isMobile ? 'column' : 'row'};
          align-items: ${isMobile ? 'stretch' : 'flex-start'};
        }

        /* Stats Preview */
        .stats-grid {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr 1fr' : '1fr 1fr'};
          gap: ${isMobile ? '1rem' : '1.5rem'};
          background: var(--off-white);
          padding: ${isMobile ? '1.5rem' : '2rem'};
          border-radius: var(--radius-lg);
          border: 1px solid var(--border-color);
        }

        .stat-preview {
          display: flex;
          align-items: center;
          gap: ${isMobile ? '0.75rem' : '1rem'};
        }

        .stat-preview-icon {
          width: ${isMobile ? '40px' : '48px'};
          height: ${isMobile ? '40px' : '48px'};
          background: var(--white);
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: ${isMobile ? '1.25rem' : '1.5rem'};
          color: var(--primary-green);
          flex-shrink: 0;
          border: 1px solid var(--border-color);
        }

        .stat-preview-content {
          flex: 1;
        }

        .stat-preview-number {
          font-size: ${isMobile ? '1.5rem' : '1.75rem'};
          font-weight: 700;
          color: var(--primary-black);
          margin-bottom: 0.25rem;
          font-family: 'Source Serif Pro', serif;
        }

        .stat-preview-label {
          font-size: ${isMobile ? '0.75rem' : '0.875rem'};
          color: var(--text-light);
          line-height: 1.4;
          font-weight: 500;
        }

        /* Scroll Indicator */
        .scroll-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          color: var(--text-light);
          font-size: 0.875rem;
        }

        .scroll-line {
          width: 1px;
          height: 40px;
          background: var(--border-color);
          position: relative;
        }

        .scroll-line::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 20px;
          background: var(--primary-green);
          animation: scroll 2s infinite;
        }

        /* Sections Common */
        section {
          padding: ${isMobile ? '80px 0' : '120px 0'};
          scroll-margin-top: var(--header-height);
        }

        .section-intro {
          text-align: center;
          max-width: 700px;
          margin: 0 auto ${isMobile ? '2rem' : '3rem'};
        }

        /* Benefits Section */
        .benefits-section {
          background: var(--light-gray);
        }

        .benefits-grid {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'};
          gap: ${isMobile ? '1.5rem' : '2rem'};
        }

        .benefit-card {
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: ${isMobile ? '1.5rem 1.25rem' : '2rem 1.75rem'};
          text-align: center;
          transition: var(--transition);
        }

        .benefit-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary-green);
        }

        .benefit-icon {
          font-size: ${isMobile ? '2rem' : '2.5rem'};
          color: var(--primary-green);
          margin-bottom: ${isMobile ? '1rem' : '1.25rem'};
        }

        .benefit-card h3 {
          margin-bottom: ${isMobile ? '0.5rem' : '0.75rem'};
        }

        .benefit-card p {
          font-size: ${isMobile ? '0.95rem' : '1rem'};
          color: var(--text-light);
          line-height: 1.6;
          margin: 0;
        }

        /* Jobs Section */
        .jobs-section {
          background: var(--white);
        }

        .jobs-grid {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'};
          gap: ${isMobile ? '1.5rem' : '2rem'};
        }

        .job-card {
          background: var(--white);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: ${isMobile ? '1.5rem' : '2rem'};
          transition: var(--transition);
          display: flex;
          flex-direction: column;
        }

        .job-card:hover {
          box-shadow: var(--shadow-md);
          border-color: var(--primary-green);
        }

        .job-header {
          margin-bottom: 1.5rem;
        }

        .job-badge {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .job-type, .job-department {
          padding: 4px 12px;
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .job-type {
          background: rgba(46, 139, 87, 0.1);
          color: var(--primary-green);
        }

        .job-department {
          background: rgba(0, 0, 0, 0.05);
          color: var(--text-secondary);
        }

        .job-title {
          margin-bottom: 0.75rem;
        }

        .job-description {
          font-size: 0.95rem;
          color: var(--text-light);
          line-height: 1.6;
        }

        .job-details {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : 'repeat(2, 1fr)'};
          gap: 0.75rem;
          margin-bottom: 1.5rem;
          padding: 1rem 0;
          border-top: 1px solid var(--border-color);
          border-bottom: 1px solid var(--border-color);
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .detail-item svg {
          color: var(--primary-green);
          flex-shrink: 0;
        }

        .compensation-paid {
          color: var(--primary-green);
          font-weight: 600;
        }

        .compensation-unpaid {
          color: #FF8C00;
          font-weight: 600;
        }

        .certificate-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(46, 139, 87, 0.1);
          padding: 6px 12px;
          border-radius: var(--radius-sm);
          margin-bottom: 1rem;
          width: fit-content;
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--primary-green);
        }

        .certificate-badge svg {
          font-size: 0.875rem;
        }

        .job-actions {
          display: flex;
          gap: 1rem;
          margin-top: auto;
        }

        .job-expanded {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--border-color);
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .job-responsibilities,
        .job-requirements,
        .job-perks {
          margin-bottom: 1.5rem;
        }

        .job-expanded h4 {
          margin-bottom: 0.75rem;
          color: var(--primary-green);
          font-size: 1.125rem;
        }

        .job-expanded ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .job-expanded li {
          padding: 0.25rem 0;
          color: var(--text-secondary);
          position: relative;
          padding-left: 1.5rem;
        }

        .job-expanded li::before {
          content: '•';
          color: var(--primary-green);
          position: absolute;
          left: 0;
          font-weight: bold;
        }

        /* Process Section */
        .process-section {
          background: var(--light-gray);
        }

        .process-steps {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : 'repeat(5, 1fr)'};
          gap: ${isMobile ? '2rem' : '1.5rem'};
          max-width: 1000px;
          margin: 0 auto;
        }

        .process-step {
          text-align: center;
          position: relative;
        }

        .process-step:not(:last-child)::after {
          content: '';
          position: absolute;
          top: 30px;
          right: -${isMobile ? '15px' : '12px'};
          width: ${isMobile ? '30px' : '24px'};
          height: 2px;
          background: var(--border-color);
          display: ${isMobile ? 'none' : 'block'};
        }

        .step-number {
          width: 60px;
          height: 60px;
          background: var(--primary-green);
          color: var(--white);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 auto 1.5rem;
          font-family: 'Source Serif Pro', serif;
        }

        .process-step h3 {
          margin-bottom: 0.5rem;
          font-size: 1.125rem;
        }

        .process-step p {
          font-size: 0.875rem;
          color: var(--text-light);
          line-height: 1.6;
        }

        /* CTA Section */
        .cta-section {
          background: var(--white);
        }

        .cta-content {
          text-align: center;
          max-width: 700px;
          margin: 0 auto;
        }

        .cta-content h2 {
          margin-bottom: ${isMobile ? '0.75rem' : '1rem'};
        }

        .cta-content p {
          font-size: ${isMobile ? '1.063rem' : '1.125rem'};
          margin-bottom: ${isMobile ? '2rem' : '2.5rem'};
          color: var(--text-secondary);
        }

        .cta-actions {
          display: flex;
          gap: ${isMobile ? '0.75rem' : '1rem'};
          justify-content: center;
          margin-bottom: ${isMobile ? '2rem' : '2.5rem'};
          flex-direction: ${isMobile ? 'column' : 'row'};
          align-items: ${isMobile ? 'stretch' : 'center'};
        }

        .cta-actions .btn-primary,
        .cta-actions .btn-secondary {
          min-width: ${isMobile ? '100%' : '200px'};
        }

        .cta-info {
          border-top: 1px solid var(--border-color);
          padding-top: 1.5rem;
          font-size: 0.875rem;
          color: var(--text-light);
        }

        .cta-info p {
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        /* Responsive Adjustments */
        @media (max-width: 768px) {
          .jobs-grid {
            grid-template-columns: 1fr;
          }

          .process-steps {
            grid-template-columns: 1fr;
            gap: 2.5rem;
          }

          .process-step:not(:last-child)::after {
            display: block;
            right: auto;
            top: auto;
            bottom: -25px;
            left: 50%;
            transform: translateX(-50%);
            width: 2px;
            height: 25px;
          }
        }

        @media (max-width: 480px) {
          .job-details {
            grid-template-columns: 1fr;
          }

          .job-actions {
            flex-direction: column;
          }

          .hero-title {
            font-size: 2rem;
          }

          h2 {
            font-size: 1.5rem;
          }
        }

        @media (min-width: 1025px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .benefits-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        /* Animations */
        @keyframes scroll {
          0% {
            transform: translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateY(20px);
            opacity: 0;
          }
        }

        /* High Contrast Mode Support */
        @media (prefers-contrast: high) {
          .job-card,
          .benefit-card {
            border: 2px solid var(--primary-black);
          }
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
          
          .scroll-line::after {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

export default Careers;
